/**
 * Detection for errors thrown by scripts that are provably NOT part of our
 * bundle: browser-extension content scripts and the binding shims injected by
 * automation browsers (BetterWright/CloakBrowser inject Playwright-style
 * bindings and patch DOM globals; when the CDP binding is missing in a frame
 * the patched API throws e.g.
 * "this._global[this._globalBindingName] is not a function").
 *
 * When such a patched API is invoked during React's render/commit phase, the
 * error unwinds into our root error boundary and — without this filter —
 * replaces the entire app with the fatal "Application Error" card, even though
 * nothing in our code is broken.
 *
 * Detection is deliberately conservative so real app errors are never
 * swallowed. A top stack frame referencing our own origin ALWAYS classifies
 * the error as ours — even when the message happens to echo an injected
 * marker (a URL, config value, or user-supplied string containing e.g.
 * `__playwright`). Otherwise the error is foreign when:
 *  - the message references the injected binding shim (`_globalBindingName` /
 *    playwright / puppeteer markers — strings that do not exist in our
 *    bundle) AND a live automation BindingsController sits on the page
 *    global. Stealth injections scrub their stack traces, so no frame
 *    evidence is required — but they cannot scrub the controller their shims
 *    dispatch through, so its presence is the corroboration instead. Or:
 *  - the THROW SITE (topmost stack frame) is an extension scheme, an
 *    automation utility script, or an injected anonymous/VM script.
 * An error thrown by our own code always has our bundle URL (or a dev-server
 * `/src/` path) as its top frame and never matches either signal.
 */

/**
 * Message signatures of injected automation shims. `_globalBindingName` is the
 * CloakBrowser/Playwright-style binding dispatcher; none of these strings
 * occur anywhere in our own bundle.
 */
const INJECTED_BINDING_MESSAGE_RE = /_globalBindingName|__playwright|__puppeteer/;

/**
 * Frame locations that cannot be our code:
 *  - extension schemes (content scripts)
 *  - puppeteer/playwright evaluation & utility scripts
 *  - `VM<n>:line:col` — scripts injected via CDP with no source URL
 *  - `<anonymous>:line:col` — eval'd/injected code. Native builtin frames
 *    render as `(<anonymous>)` WITHOUT a line:col, so requiring the digits
 *    keeps e.g. `at Array.forEach (<anonymous>)` out of this match.
 */
const FOREIGN_FRAME_RE =
  /chrome-extension:\/\/|moz-extension:\/\/|safari(?:-web)?-extension:\/\/|__puppeteer_evaluation_script__|pptr:|__playwright|UtilityScript|\bVM\d+:\d+|<anonymous>:\d+/;

/**
 * First stack-frame line of a stack trace. Chromium frames are indented and
 * start with "at "; Firefox/Safari frames look like `fn@url:line:col`. Lines
 * before the first frame (the error message, possibly multiline) are skipped.
 */
function topStackFrame(stack: string): string | undefined {
  return stack
    .split("\n")
    .map((line) => line.trim())
    .find((line) => line.startsWith("at ") || /^\S*@\S+:\d+:\d+$/.test(line));
}

/** Characters that may precede our origin and still leave it starting a URL token. */
const OWN_ORIGIN_PREFIX_RE = /[\s(@]/;

/**
 * A frame that references our own origin is ours even when it ends in
 * `<anonymous>:line:col` — Chromium renders first-party eval'd code as
 * `at fn (eval at caller (https://our-origin/assets/app.js:1:2), <anonymous>:1:1)`,
 * and Firefox as `fn@https://our-origin/app.js line 1 > eval:1:1`.
 */
function isOwnOriginFrame(frame: string): boolean {
  if (typeof location === "undefined") return false;
  const origin = location.origin;
  // Opaque origins serialize as the string "null"; an empty string should be
  // impossible but would make the indexOf("") scan below spin forever (the
  // search index clamps to frame.length and never returns -1), so both bail.
  if (!origin || origin === "null") return false;
  // Origin-anchored, not a bare substring match: the origin must start a URL
  // token (start of frame, whitespace, "(" or "@") and continue into a path or
  // port/line separator. A foreign frame whose URL merely embeds our origin as
  // a substring (`https://evil.example/x?ref=<our-origin>`) must not be
  // classified as ours — that would exempt a genuinely foreign error from the
  // filter.
  //
  // Scanned as plain strings rather than compiled into `new RegExp(origin)`:
  // building a pattern out of a runtime value is a ReDoS footgun even when the
  // value is escaped, and it is a real Codacy Security finding on this file.
  for (let at = frame.indexOf(origin); at !== -1; at = frame.indexOf(origin, at + 1)) {
    const startsToken = at === 0 || OWN_ORIGIN_PREFIX_RE.test(frame[at - 1]);
    const nextChar = frame[at + origin.length];
    if (startsToken && (nextChar === "/" || nextChar === ":")) return true;
  }
  return false;
}

/**
 * True when an error provably originates from a script outside our bundle.
 * Unknown/ambiguous errors (no stack, unrecognisable frames) return false so
 * they keep surfacing as real errors.
 */
export function isForeignScriptError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const top = error.stack ? topStackFrame(error.stack) : undefined;
  // Own-origin top frame is checked BEFORE the message signal: our own code
  // throwing a message that merely echoes an injected marker must not be
  // filtered as foreign.
  if (top !== undefined && isOwnOriginFrame(top)) return false;
  // The message signal needs no frame evidence (stealth injections scrub
  // their stack traces — demanding a recognisable foreign frame would miss
  // exactly the errors this filter exists for) but it is NOT trusted alone:
  // it must be corroborated by a live automation BindingsController on the
  // page global — the object the shims dispatch through, which an injection
  // cannot scrub away. Without that evidence a real app error whose message
  // merely echoes a marker (wrapped third-party text, a URL) and whose stack
  // lost its top frame keeps surfacing as a real error, with the boundary's
  // normal reset cleanup intact. This also aligns detection with repair: no
  // findable controller means repairBrokenAutomationBindings() could not fix
  // anything and auto-retrying would only burn the budget.
  if (INJECTED_BINDING_MESSAGE_RE.test(error.message) && hasAutomationBindingsController()) {
    return true;
  }
  if (top === undefined) return false;
  return FOREIGN_FRAME_RE.test(top);
}

/**
 * Duck-typed shape of playwright-core's injected page-world
 * `BindingsController` (`packages/injected/src/bindingsController.ts`). Every
 * binding shim it installs dispatches through
 * `this._global[this._globalBindingName](...)` — a fresh lookup on every call.
 */
interface AutomationBindingsController {
  _globalBindingName: string;
  _global: Record<string, unknown>;
}

function isAutomationBindingsController(value: unknown): value is AutomationBindingsController {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as { _globalBindingName?: unknown; _global?: unknown };
  // `_global` must be the page's global object — that is the automation-specific
  // marker beyond the property names. A CDP `Runtime.addBinding` dispatch
  // function only ever lives on the real global, and playwright-style
  // controllers are constructed with it. A third-party object that duck-types
  // the shape but dispatches into its own namespace object never qualifies, so
  // it can never receive a stub (which could otherwise block a lazily-installed
  // real function via the library's own "skip if already present" check).
  return typeof candidate._globalBindingName === "string" && candidate._global === window;
}

/** Every automation BindingsController reachable as an own property of window. */
function findAutomationBindingsControllers(): AutomationBindingsController[] {
  const controllers: AutomationBindingsController[] = [];
  const host = window as unknown as Record<string, unknown>;
  for (const name of Object.getOwnPropertyNames(window)) {
    let value: unknown;
    try {
      value = host[name];
    } catch {
      continue; // some window getters throw on access
    }
    if (isAutomationBindingsController(value)) controllers.push(value);
  }
  return controllers;
}

/**
 * Page-global corroboration for the message-only foreign-error signal: an
 * automation layer that injects binding shims necessarily leaves its
 * BindingsController on the page global, however thoroughly it scrubs stack
 * traces. Only runs when an error message already matched a marker.
 */
function hasAutomationBindingsController(): boolean {
  return findAutomationBindingsControllers().length > 0;
}

/**
 * Repair automation-injected binding shims whose dispatch global is missing.
 *
 * Playwright-style automation layers (BetterWright/CloakBrowser) install a
 * page-world `BindingsController` plus API shims (observed live: the WebSocket
 * routing mock replaces `window.WebSocket`, and its constructor dispatches via
 * the controller). The dispatch target is a CDP `Runtime.addBinding` global;
 * when the stealth layer never registers it in this world, EVERY shim call
 * throws "this._global[this._globalBindingName] is not a function" — e.g.
 * `new WebSocket(...)` from Supabase realtime, synchronously inside a React
 * commit, persistently on every retry.
 *
 * Wrapping the shims does not help — consumers capture direct references. But
 * the shim re-reads `_global[_globalBindingName]` on each call, so installing
 * a no-op stub for the missing dispatch global fixes every consumer at once
 * (verified live on the broken page: `new WebSocket` constructs and the app
 * renders). If the automation layer later registers the real CDP binding it
 * simply overwrites the stub. For real users no controller exists and this
 * scans a few hundred window properties once, doing nothing.
 *
 * The repair is strictly ADDITIVE and gated on the automation marker: only a
 * controller whose `_global` IS the page global (see
 * {@link isAutomationBindingsController}) is considered, and the stub is
 * installed only when the dispatch property is entirely absent from it. A CDP
 * `Runtime.addBinding` global that was never registered is absent, so the
 * broken case is still repaired — but an object that merely duck-types like a
 * controller (any `{_globalBindingName, _global}` pair a library might
 * expose) is either skipped outright (own-namespace `_global`) or can never
 * have an EXISTING value overwritten, whatever its type.
 *
 * Returns the number of controllers whose dispatch global was stubbed.
 */
export function repairBrokenAutomationBindings(): number {
  let repaired = 0;
  for (const controller of findAutomationBindingsControllers()) {
    if (controller._globalBindingName in controller._global) continue;
    controller._global[controller._globalBindingName] = () => undefined;
    repaired++;
  }
  return repaired;
}

const RETRY_WINDOW_MS = 10_000;
const MAX_RETRIES_PER_WINDOW = 5;

let retryTimestamps: number[] = [];

/**
 * Per-error verdict cache making {@link consumeForeignErrorRetry} idempotent:
 * the fallback component calls it during render, and React can re-render the
 * fallback several times for one caught error (StrictMode double-invoke, a
 * parent re-render during the auto-reset delay). Only the first call per error
 * object touches the budget; repeats return the same verdict.
 */
const retryVerdicts = new WeakMap<Error, boolean>();

/**
 * Bounded retry budget for auto-recovering from foreign-script errors: true
 * while fewer than {@link MAX_RETRIES_PER_WINDOW} distinct errors were retried
 * in the last {@link RETRY_WINDOW_MS}. Keeps a persistently-broken injection
 * from looping the boundary forever — once the budget is exhausted the fatal
 * card shows, which is exactly the pre-filter behaviour.
 *
 * The budget is module-global by design: the app mounts a single root
 * ContextErrorBoundary, and the cap is on total auto-reset churn. That stays
 * the right semantics even if additional boundaries ever consume it — a
 * foreign injection is page-global, so its churn budget is shared page-wide,
 * not per boundary. Don't make this per-instance.
 */
export function consumeForeignErrorRetry(error: Error, now: number = Date.now()): boolean {
  const cached = retryVerdicts.get(error);
  if (cached !== undefined) return cached;
  retryTimestamps = retryTimestamps.filter((t) => now - t < RETRY_WINDOW_MS);
  const allowed = retryTimestamps.length < MAX_RETRIES_PER_WINDOW;
  if (allowed) retryTimestamps.push(now);
  retryVerdicts.set(error, allowed);
  return allowed;
}
